import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';
import { Search, Plus, MoreVertical, Edit, Trash2, UserCheck, UserX } from 'lucide-react';
import {
  ActionMenu,
  Avatar,
  Badge,
  Button,
  IconButton,
  Input,
  Spinner,
  Table,
  type BadgeProps,
} from '@ntconsult/aluma-react';
import { EmptyState } from '@common/components/EmptyState';
import { ConfirmDialog } from '@common/components/ConfirmDialog';
import { useDebouncedValue } from '@common/hooks/useDebouncedValue';
import { useDeleteConfirmation } from '@common/hooks/useDeleteConfirmation';
import { useIsMobile } from '@common/hooks/useMediaQuery';
import { formatDateTime } from '@common/utils/format';
import { useUsers } from '../hooks/useUsers';
import { useDeleteUser } from '../hooks/useDeleteUser';
import { useUpdateUser } from '../hooks/useUpdateUser';
import { UserFormModal } from './UserFormModal';
import type { User, UserRole } from '../types';

const roleTones: Record<UserRole, BadgeProps['tone']> = {
  admin: 'info',
  manager: 'warning',
  user: 'neutral',
};

const PAGE_SIZE = 20;

const userFilterParsers = {
  search: parseAsString.withDefault(''),
  page: parseAsInteger.withDefault(1),
};

export function UserList() {
  const { t } = useTranslation(['users', 'common']);
  const [{ search, page }, setFilters] = useQueryStates(userFilterParsers, {
    history: 'replace',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const debouncedSearch = useDebouncedValue(search, 400);
  const isMobile = useIsMobile();
  const { data, isLoading } = useUsers({
    search: debouncedSearch,
    page,
    limit: PAGE_SIZE,
  });
  const { mutate: deleteUser } = useDeleteUser();
  const { mutate: updateUser } = useUpdateUser();
  const deleteConfirmation = useDeleteConfirmation();

  const users = data?.data || [];
  const total = data?.meta?.total ?? 0;
  const totalPages = data?.meta?.totalPages ?? 1;

  const roleLabels: Record<UserRole, string> = {
    admin: t('users:roles.admin'),
    manager: t('users:roles.manager'),
    user: t('users:roles.user'),
  };

  const handleDelete = (user: User) => {
    deleteConfirmation.requestDelete(user.name, () => deleteUser(user.id));
  };

  const handleToggleActive = (user: User) => {
    updateUser({ id: user.id, data: { isActive: !user.isActive } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-foreground">{t('users:title')}</h1>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex-1 min-w-0 sm:flex-none sm:w-64">
            <Input
              type="text"
              value={search}
              onChange={(e) =>
                void setFilters({ search: e.target.value || null, page: 1 })
              }
              placeholder={t('users:searchPlaceholder')}
              leftIcon={<Search className="w-4 h-4" aria-hidden="true" />}
            />
          </div>

          <Button
            onClick={() => setShowCreateModal(true)}
            aria-label={t('users:newUser')}
            className="shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('users:newUser')}</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : users.length === 0 ? (
        <EmptyState
          title={t('users:noUsers')}
          description={search ? t('users:noUsersSearch') : t('users:noUsersEmpty')}
          action={
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4" />
              {t('users:newUser')}
            </Button>
          }
        />
      ) : (
        <>
          {isMobile ? (
            <ul
              role="list"
              className="bg-surface rounded-lg border border-border divide-y divide-border overflow-hidden"
            >
              {users.map((user) => (
                <li key={user.id} className="flex items-center gap-3 px-3 py-3 min-h-14">
                  <Avatar name={user.name} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-foreground truncate">{user.name}</span>
                    </div>
                    <div className="text-xs text-foreground-muted truncate">{user.email}</div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <Badge variant="soft" tone={roleTones[user.role]}>
                        {roleLabels[user.role]}
                      </Badge>
                      <Badge variant="soft" tone={user.isActive ? 'success' : 'danger'}>
                        {user.isActive ? t('common:status.active') : t('common:status.inactive')}
                      </Badge>
                    </div>
                  </div>
                  <ActionMenu
                    align="right"
                    trigger={
                      <IconButton
                        variant="ghost"
                        size="sm"
                        label={t('common:actions.openMenu', { defaultValue: 'Abrir menu' })}
                        icon={<MoreVertical className="w-4 h-4" aria-hidden="true" />}
                      />
                    }
                  >
                    <ActionMenu.Item
                      icon={<Edit className="w-4 h-4" aria-hidden="true" />}
                      onClick={() => setEditingUser(user)}
                    >
                      {t('common:actions.edit')}
                    </ActionMenu.Item>
                    <ActionMenu.Item
                      icon={
                        user.isActive ? (
                          <UserX className="w-4 h-4" aria-hidden="true" />
                        ) : (
                          <UserCheck className="w-4 h-4" aria-hidden="true" />
                        )
                      }
                      onClick={() => handleToggleActive(user)}
                    >
                      {user.isActive ? t('users:actions.deactivate') : t('users:actions.activate')}
                    </ActionMenu.Item>
                    <ActionMenu.Separator />
                    <ActionMenu.Item
                      variant="danger"
                      icon={<Trash2 className="w-4 h-4" aria-hidden="true" />}
                      onClick={() => handleDelete(user)}
                    >
                      {t('common:actions.delete')}
                    </ActionMenu.Item>
                  </ActionMenu>
                </li>
              ))}
            </ul>
          ) : (
            <Table containerClassName="bg-surface rounded-lg border border-border">
              <Table.Head>
                <Table.Row>
                  <Table.Header>{t('users:title')}</Table.Header>
                  <Table.Header>{t('users:table.email')}</Table.Header>
                  <Table.Header className="w-32">{t('users:table.role')}</Table.Header>
                  <Table.Header className="w-28">{t('users:table.status')}</Table.Header>
                  <Table.Header className="w-40">{t('users:table.lastAccess')}</Table.Header>
                  <Table.Header className="w-16"></Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {users.map((user) => (
                  <Table.Row key={user.id}>
                    <Table.Cell>
                      <div className="flex items-center gap-3">
                        <Avatar name={user.name} size="sm" />
                        <span className="font-medium text-foreground">{user.name}</span>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="text-foreground-muted">{user.email}</Table.Cell>
                    <Table.Cell>
                      <Badge variant="soft" tone={roleTones[user.role]}>{roleLabels[user.role]}</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant="soft" tone={user.isActive ? 'success' : 'danger'}>
                        {user.isActive ? t('common:status.active') : t('common:status.inactive')}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell className="text-foreground-muted">
                      {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : t('common:time.never')}
                    </Table.Cell>
                    <Table.Cell>
                      <ActionMenu
                        align="right"
                        trigger={
                          <IconButton
                            variant="ghost"
                            size="sm"
                            label={t('common:actions.openMenu', { defaultValue: 'Abrir menu' })}
                            icon={<MoreVertical className="w-4 h-4" aria-hidden="true" />}
                          />
                        }
                      >
                        <ActionMenu.Item
                          icon={<Edit className="w-4 h-4" aria-hidden="true" />}
                          onClick={() => setEditingUser(user)}
                        >
                          {t('common:actions.edit')}
                        </ActionMenu.Item>
                        <ActionMenu.Item
                          icon={
                            user.isActive ? (
                              <UserX className="w-4 h-4" aria-hidden="true" />
                            ) : (
                              <UserCheck className="w-4 h-4" aria-hidden="true" />
                            )
                          }
                          onClick={() => handleToggleActive(user)}
                        >
                          {user.isActive ? t('users:actions.deactivate') : t('users:actions.activate')}
                        </ActionMenu.Item>
                        <ActionMenu.Separator />
                        <ActionMenu.Item
                          variant="danger"
                          icon={<Trash2 className="w-4 h-4" aria-hidden="true" />}
                          onClick={() => handleDelete(user)}
                        >
                          {t('common:actions.delete')}
                        </ActionMenu.Item>
                      </ActionMenu>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-foreground-muted">
                {t('users:rangeLabel', {
                  from: (page - 1) * PAGE_SIZE + 1,
                  to: Math.min(page * PAGE_SIZE, total),
                  total,
                })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    void setFilters({ page: Math.max(1, page - 1) })
                  }
                  disabled={page === 1}
                >
                  {t('common:pagination.previous')}
                </Button>
                <span className="text-sm text-foreground-muted">
                  {t('common:pagination.page')} {page}{' '}
                  {t('common:pagination.of')} {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    void setFilters({ page: Math.min(totalPages, page + 1) })
                  }
                  disabled={page === totalPages}
                >
                  {t('common:pagination.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <UserFormModal
        isOpen={showCreateModal || !!editingUser}
        onClose={() => {
          setShowCreateModal(false);
          setEditingUser(null);
        }}
        user={editingUser}
      />

      <ConfirmDialog
        open={deleteConfirmation.isOpen}
        title={t('common:confirmDelete.title')}
        description={t('common:confirmDelete.description', {
          name: deleteConfirmation.pending?.name ?? '',
        })}
        confirmLabel={t('common:confirmDelete.confirm')}
        onConfirm={deleteConfirmation.confirm}
        onCancel={deleteConfirmation.cancel}
      />
    </div>
  );
}
